"use client";

import { cookedToPlainText } from "@/lib/processCooked";
import type { Message, MessageQuote } from "@/lib/api/messages";
import { personName } from "./participants";

/** A message with no text (e.g. an image-only message) reads as this instead. */
const IMAGE_ONLY_LABEL = "📷 Billede";

/**
 * Shown above the composer while the user is composing a reply to a specific
 * earlier message, with a way to cancel it. `message` is the ORIGINAL Message
 * being replied to (not a server-shaped preview) — it is always in hand
 * client-side, since you can only start a reply from a bubble already on screen.
 */
export function ReplyComposerBar({
  message,
  onCancel,
}: {
  message: Message;
  onCancel: () => void;
}) {
  const excerpt = cookedToPlainText(message.bodyHtml ?? "") || IMAGE_ONLY_LABEL;

  return (
    <div className="flex items-start gap-2 px-3 py-1.5 mb-1.5 rounded-lg bg-muted border-l-2 border-primary">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-primary">
          Svarer på {personName(message.author)}
        </p>
        <p className="text-xs text-muted-foreground truncate">{excerpt}</p>
      </div>
      <button
        type="button"
        onClick={onCancel}
        aria-label="Annullér svar"
        title="Annullér svar"
        className="shrink-0 p-1 text-subtle-foreground hover:text-foreground rounded transition-colors"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

/**
 * The compact, read-only quote rendered inside a message that replies to
 * another one. `replyTo` is null in two cases the caller must distinguish
 * with `message.replyToMessageId` before rendering this at all: not a reply,
 * or a reply whose target has since been deleted/hidden — this component only
 * ever sees the second case as null, and renders the unavailable-message
 * fallback.
 */
export function QuotedMessage({
  replyTo,
  mine,
}: {
  replyTo: MessageQuote | null;
  mine: boolean;
}) {
  if (!replyTo) {
    return (
      <div
        className={`mb-1.5 pl-2 border-l-2 text-xs italic ${
          mine
            ? "border-white/40 text-white/70"
            : "border-border-strong text-subtle-foreground"
        }`}
      >
        Beskeden er ikke længere tilgængelig
      </div>
    );
  }

  return (
    <div
      className={`mb-1.5 pl-2 border-l-2 text-xs ${
        mine ? "border-white/50" : "border-primary"
      }`}
    >
      <p
        className={`font-medium truncate ${
          mine ? "text-white/90" : "text-primary"
        }`}
      >
        {personName(replyTo.author)}
      </p>
      <p className={`truncate ${mine ? "text-white/70" : "text-muted-foreground"}`}>
        {replyTo.excerpt || IMAGE_ONLY_LABEL}
      </p>
    </div>
  );
}
